<template>
  <v-card>
    <div class="person__info__pre-header">
      <div class="d-flex align-center pa-2">
        <h4 class="secondary--text font-weight-bold ml-6">
          {{ $t('fleet_management.geofences.geofence_details.edit_geofence') }}
        </h4>
      </div>
      <v-divider></v-divider>
    </div>
    <v-form ref="form" v-model="valid">
      <v-container class="px-8 px-lg-8 px-xl-8 pb-6 pt-4">
        <v-row>
          <v-col cols="auto">
            <v-text-field
              v-model.trim="name"
              :counter="40"
              :label="
                $t(
                  'fleet_management.geofences.geofence_details.labels.edit_geofence_name'
                )
              "
              :rules="nameRules"
              filled
              dense
              required
              ref="geofenceName"
            ></v-text-field>
          </v-col>
        </v-row>
        <v-row>
          <v-col>
            <hr class="divider-dotted" />
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12">
            <h4 class="secondary--text">
              {{
                $t(
                  'fleet_management.geofences.geofence_details.labels.form_sub_title'
                )
              }}
            </h4>
          </v-col>
        </v-row>

        <v-row>
          <v-col md="4">
            <v-text-field
              v-model.number="latitude"
              dense
              filled
              hide-details="auto"
              @input="updateLatitude"
              :label="
                $t(
                  'fleet_management.geofences.geofence_details.labels.latitude'
                )
              "
              placeholder="-90 / 90"
              ref="latitude"
              required
              :rules="[coordinatesRules.latitude]"
            />
          </v-col>
          <v-col md="4">
            <v-text-field
              v-model.number="longitude"
              dense
              filled
              hide-details="auto"
              @input="updateLongitude"
              :label="
                $t(
                  'fleet_management.geofences.geofence_details.labels.longitude'
                )
              "
              placeholder="-180 / 180"
              ref="longitude"
              required
              :rules="[coordinatesRules.longitude]"
            ></v-text-field>
          </v-col>
          <v-col md="4">
            <v-text-field
              v-model.number="radius"
              dense
              :error-messages="radiusError"
              filled
              @input="updateRadius"
              :label="
                $t('fleet_management.geofences.geofence_details.labels.radius')
              "
              required
              ref="radius"
              :rules="[
                radiusRules.required,
                radiusRules.format,
                radiusRules.range
              ]"
            ></v-text-field>
          </v-col>
        </v-row>
        <v-row class="footer">
          <v-col>
            <v-row justify="space-between">
              <v-col cols="auto">
                <v-btn
                  :to="{ name: 'geofences' }"
                  outlined="true"
                  color="secondary"
                  rounded
                  >{{
                    $t(
                      'fleet_management.geofences.geofence_details.labels.form_edit_geofence_cancel_button'
                    )
                  }}</v-btn
                >
              </v-col>
              <v-col cols="auto">
                <v-btn
                  color="tertiary white--text"
                  rounded
                  :disabled="!valid || formHasNotChanged"
                  @click="saveEditedZone"
                  >{{
                    $t(
                      'fleet_management.geofences.geofence_details.labels.form_edit_geofence_accept_button'
                    )
                  }}</v-btn
                >
              </v-col>
            </v-row>
          </v-col>
        </v-row>
      </v-container>
    </v-form>
    <Dialog
      v-if="showDialog"
      idCancelButton="idBtnZonasCancelarModificacion"
      idSubmitButton="idBtnZonasAceptarModificacion"
      @cancelButtonPressed="showDialog = false"
      :loading="dialog.loading"
      :showDialog="showDialog"
      @submitButtonPressed="dialog.submitAction"
      :text="dialog.text"
      :title="dialog.title"
      type="warning"
    />
  </v-card>
</template>

<script>
import { GEOFENCE_MIN_RADIUS, GEOFENCE_MAX_RADIUS } from '@leapsight/styleguide'
import { i18n } from '@leapsight/portal'
import Dialog from '@/components/dialog/Dialog'
import { GeofencesHelpers } from '@/helpers/mainExports.js'
import { mapGetters } from 'vuex'
import store from '@/store'

export default {
  components: {
    Dialog
  },
  props: {
    geofenceData: {
      type: Object
    }
  },
  data() {
    return {
      circle: null,
      coordinatesRules: {
        latitude: value => {
          const regex = /^(\+|-)?(?:90(?:(?:\.0{1,7})?)|(?:[0-9]|[1-8][0-9])(?:(?:\.[0-9]{1,7})?))$/
          const result = regex.test(value)
          //Si no cumple con el formato de coordenada, verifico que sea un número real lo ingresado
          if (!result) {
            const isNumberRegex = /^-?[0-9]\d*(\.\d+)?$/
            const isNumber = isNumberRegex.test(value)
            if (isNumber && value > 90)
              return i18n.t(
                'fleet_management.geofences.create_geofence.rules.max_latitude'
              )
            else if (isNumber && value < -90)
              return i18n.t(
                'fleet_management.geofences.create_geofence.rules.min_latitude'
              )
            else if (!isNumber)
              //No es un número
              return i18n.t('fleet_management.geofences.coordinates.bad_format')
          }
          return result
        },
        longitude: value => {
          const regex = /^(\+|-)?(?:180(?:(?:\.0{1,7})?)|(?:[0-9]|[1-9][0-9]|1[0-7][0-9])(?:(?:\.[0-9]{1,7})?))$/
          const result = regex.test(value)
          if (!result) {
            const isNumberRegex = /^-?[0-9]\d*(\.\d+)?$/
            const isNumber = isNumberRegex.test(value)
            if (isNumber && value > 180)
              return i18n.t(
                'fleet_management.geofences.create_geofence.rules.max_longitude'
              )
            else if (isNumber && value < -180)
              return i18n.t(
                'fleet_management.geofences.create_geofence.rules.min_longitude'
              )
            else if (!isNumber)
              //No es un número
              return i18n.t('fleet_management.geofences.coordinates.bad_format')
          }
          return result
        }
      },
      dialog: {
        loading: false,
        text: '',
        title: '',
        type: '',
        submitAction: ''
      },
      id: null,
      name: '',
      latitude: null,
      longitude: null,
      radius: null,
      loadingDialog: false,
      nameRules: [
        v =>
          !!v ||
          i18n.t('fleet_management.persons.new_person.rules.name_required'),
        v =>
          (v && v.length <= 40) ||
          i18n.t(
            'fleet_management.geofences.create_geofence.rules.character_length'
          ),
        v =>
          (v && v.length >= 2) ||
          i18n.t(
            'fleet_management.geofences.create_geofence.rules.character_length'
          ),
        v =>
          /^[a-zA-Z0-9áéíóúüÁÉÍÓÚÜñÑçÇâêîôûÂÊÎÔÛàèìòùÀÈÌÒÙäöüÄÖÜßëïÿËÏŸ#$&^\[\]_ -.\\s]+$/gi.test(
            v
          ) || i18n.t('fleet_management.agents.create.errors.invalid_name')
      ],
      oldGeofence: {},
      radiusRules: {
        required: value =>
          !!value ||
          i18n.t(
            'fleet_management.geofences.create_geofence.rules.field_required'
          ),
        range: value => {
          if (value > GEOFENCE_MAX_RADIUS)
            return i18n.t(
              'fleet_management.geofences.circular_geofence.rules.max_radius',
              {
                GEOFENCE_MAX_RADIUS
              }
            )
          else if (value < GEOFENCE_MIN_RADIUS)
            return i18n.t(
              'fleet_management.geofences.circular_geofence.rules.min_radius',
              {
                GEOFENCE_MIN_RADIUS
              }
            )
          else return !!value
        },
        format: value => {
          const regex = /^\d+$/
          return (
            regex.test(value) ||
            i18n.t('fleet_management.geofences.coordinates.bad_format')
          )
        }
      },
      radiusError: '',
      routeTo: { name: 'GeofencesList' },
      showDialog: false,
      valid: true
    }
  },
  methods: {
    getGeofence(currentRoute) {
      const response = currentRoute
      this.loading = false
      if (!response.hasOwnProperty('error')) {
        this.id = response.id
        this.latitude = Number(response.data.geometry.coordinates[0].toFixed(7))

        this.longitude = Number(
          response.data.geometry.coordinates[1].toFixed(7)
        )
        this.radius = Number(response.data.geometry.radius.toFixed(0))
        this.name = response.data.properties.name

        this.oldGeofence = Object.assign({}, response.data)
        this.newCircle()
      }
    },
    newCircle() {
      const lat = this.latitude
      const lng = this.longitude
      const radius = this.radius
      const dataToCircle = {
        geometry: {
          coordinates: [lat, lng],
          radius
        }
      }
      this.circle = GeofencesHelpers.showGeofenceOnMap(
        dataToCircle,
        'circle',
        true
      )
      google.maps.event.addListener(this.circle, 'radius_changed', () => {
        // if (this.circle.getRadius() > GEOFENCE_MAX_RADIUS) {
        //   this.circle.setRadius(GEOFENCE_MAX_RADIUS)
        //   this.radius = GEOFENCE_MAX_RADIUS
        // } else if (this.circle.getRadius() < GEOFENCE_MIN_RADIUS) {
        //   this.circle.setRadius(GEOFENCE_MIN_RADIUS)
        //   this.radius = GEOFENCE_MIN_RADIUS
        // }
        // this.radius = this.circle.getRadius().toFixed()
        const circleRadius = Number(this.circle.getRadius().toFixed())
        if (circleRadius > GEOFENCE_MAX_RADIUS) {
          this.circle.setRadius(GEOFENCE_MAX_RADIUS)
          this.radius = GEOFENCE_MAX_RADIUS
        } else if (circleRadius < GEOFENCE_MIN_RADIUS) {
          this.circle.setRadius(GEOFENCE_MIN_RADIUS)
          this.radius = GEOFENCE_MIN_RADIUS
        } else this.radius = circleRadius
      })
      google.maps.event.addListener(this.circle, 'center_changed', () => {
        this.latitude = Number(
          this.circle
            .getCenter()
            .lat()
            .toFixed(7)
        )
        this.longitude = Number(
          this.circle
            .getCenter()
            .lng()
            .toFixed(7)
        )
      })
    },
    saveZone() {
      this.dialog.loading = true
      const payload = {
        id: this.id,
        type: 'Feature',
        geometry: {
          type: 'Circle',
          coordinates: [this.latitude, this.longitude],
          radius: Number(this.radius)
        },
        properties: {
          radius_units: 'm',
          name: this.name
        }
      }
      GeofencesHelpers.updateCircle(payload)
        .then(() => {
          this.dialog.loading = false
          this.showDialog = false
          this.$router.push({ name: 'geofences' })
          const snackbar = {
            color: 'success',
            text: i18n.t(
              'fleet_management.geofences.geofence_details.alerts.geofence_edited'
            )
          }
          store.dispatch('ui/setSnackbar', snackbar)
        })
        .catch(error => {
          this.dialog.loading = false
          this.showDialog = false
          const errorText = GeofencesHelpers.getErrorMessageByCode(error.error)
          const snackbar = {
            text: errorText
          }
          store.dispatch('ui/setSnackbar', snackbar)
        })
    },
    saveEditedZone() {
      if (this.$refs.form.validate()) {
        this.showDialog = true
        this.dialog.text = i18n.t(
          'fleet_management.geofences.geofence_details.alerts.card_text_confirm_edit_geofence'
        )
        this.dialog.title = i18n.t(
          'fleet_management.geofences.geofence_details.alerts.attention'
        )
        this.dialog.submitAction = this.saveZone
      }
    },
    updateRadius(val) {
      //Círculo creado?
      if (this.circle !== null) {
        if (
          this.radius >= GEOFENCE_MIN_RADIUS &&
          this.radius <= GEOFENCE_MAX_RADIUS
        )
          this.circle.setRadius(Number(val))
      }
      this.updateMapCircle()
    },
    updateLatitude(val) {
      if (val.indexOf(' ') == -1)
        if (!isNaN(val) && val !== '') {
          this.latitude = Number(parseFloat(val).toFixed(7))
          this.updateMapCircle()
        }
    },
    updateLongitude(val) {
      if (val.indexOf(' ') == -1)
        if (!isNaN(val) && val !== '') {
          this.longitude = Number(parseFloat(val).toFixed(7))
          this.updateMapCircle()
        }
    },
    updateMapCircle() {
      const latitudeHasError = this.$refs.latitude.validate()
      const longitudeHasError = this.$refs.longitude.validate()
      const radiusHasError = this.$refs.radius.validate()
      if (latitudeHasError && longitudeHasError && radiusHasError) {
        //Si los inputs no presentan errores tengo que crear o actualizar el mapa
        //Circulo Creado
        if (this.circle !== null) {
          const center = new google.maps.LatLng(this.latitude, this.longitude)
          this.circle.setCenter(center)
          GeofencesHelpers.getMap().fitBounds(this.circle.getBounds())
        }
      }
    }
  },
  computed: {
    ...mapGetters('auth', ['session']),
    formHasNotChanged() {
      let value = true
      if (this.name !== '')
        if (this.name !== this.oldGeofence.properties.name) value = false

      if (typeof this.oldGeofence.geometry !== 'undefined') {
        if (
          this.latitude !==
          Number(this.oldGeofence.geometry.coordinates[0].toFixed(7))
        )
          value = false

        if (
          this.longitude !==
          Number(this.oldGeofence.geometry.coordinates[1].toFixed(7))
        )
          value = false

        if (Number(this.radius) !== this.oldGeofence.geometry.radius)
          value = false
      }

      return value
    }
  },

  created() {
    this.getGeofence(this.geofenceData)
  },
  beforeDestroy() {
    //El condicional asegura que hayamos tenido una vista donde se renderizo el mapa
    //Frente a un error del request getGeofense no se llega a crear el mismo
    if (Object.keys(this.circle).length > 0) {
      this.circle.setMap(null)
      const map = GeofencesHelpers.getMap()
      google.maps.event.clearListeners(map, 'click')
    }
  }
}
</script>

<style lang="scss" scoped>
.orange--text {
  color: var(--orange) !important;
}
.dialog-attention {
  border: 2px solid var(--orange);
}
</style>
